import { getHubSpotPropertyOptions } from "./client";
import { HubSpotCompany, HubSpotPropertyOption, HubSpotRawOption } from "./types";
import { batchReadAssociations } from "@/lib/hubspot/actions-associations";
import {
  HubSpotCompanyServiceError,
  getCompanyFromServiceV2,
  searchCompaniesFromService,
} from "./server/company-service-client";
import {
  parseHubSpotCompanySearchPage,
  parseHubSpotProblemDetails,
  toLegacyHubSpotCompany,
} from "./company-contract";
import {
  fetchGatewayWithSessionRecovery,
  GatewayAuthRequiredError,
} from "@/lib/auth/gateway";
import {
  HUBSPOT_COMPANY_PRELOAD_LIMIT,
  isHubSpotCompanyPreloadSegment,
  type HubSpotCompanyPreloadSegment,
} from "./company-preload";
const COMPANY_PRELOAD_TIMEOUT_MS = 25_000;

function buildPreloadServiceUrl(baseUrl: string, segment: HubSpotCompanyPreloadSegment, limit: number): URL {
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const parsedBaseUrl = new URL(normalizedBaseUrl);
  if (parsedBaseUrl.protocol !== "http:" && parsedBaseUrl.protocol !== "https:") {
    throw new Error("HUBSPOT_INTERNAL_BASE_URL must use HTTP or HTTPS");
  }
  const url = new URL("api/hubspot/v1/companies/preload", normalizedBaseUrl);
  url.searchParams.set("segment", segment);
  url.searchParams.set("limit", String(limit));
  return url;
}
function readProblemDetailsCode(value: Parameters<typeof parseHubSpotProblemDetails>[0], status: number) {
  const problemDetails = parseHubSpotProblemDetails(value);
  return problemDetails?.status === status ? problemDetails.code : null;
}

export async function preloadCompaniesFromService(
  segment: HubSpotCompanyPreloadSegment,
  limit = HUBSPOT_COMPANY_PRELOAD_LIMIT,
): Promise<HubSpotCompany[]> {
  if (!isHubSpotCompanyPreloadSegment(segment) || !Number.isInteger(limit)
      || limit < 1 || limit > HUBSPOT_COMPANY_PRELOAD_LIMIT) {
    throw new HubSpotCompanyServiceError(400, "INVALID_PRELOAD_REQUEST");
  }

  const url = buildPreloadServiceUrl("http://gateway.local", segment, limit);

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), COMPANY_PRELOAD_TIMEOUT_MS);
  const correlationId = crypto.randomUUID();
  let response: Response;
  let payload: unknown;
  try {
    response = await fetchGatewayWithSessionRecovery(`${url.pathname}${url.search}`, {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "X-Correlation-Id": correlationId,
      },
      signal: timeoutController.signal,
    });
    payload = await response.json();
  } catch (error) {
    if (error instanceof GatewayAuthRequiredError) {
      throw new HubSpotCompanyServiceError(401, "AUTH_REQUIRED");
    }
    if (timeoutController.signal.aborted) {
      throw new HubSpotCompanyServiceError(504, "HUBSPOT_TIMEOUT");
    }
    console.error("[hubspot-company-preload] Internal service request failed", {
      cause: error instanceof Error ? error.name : "unknown",
    });
    throw new HubSpotCompanyServiceError(502, "HUBSPOT_PRELOAD_UNAVAILABLE");
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok || response.status !== 200) {
    const code = readProblemDetailsCode(payload, response.status);
    if (!code) {
      throw new HubSpotCompanyServiceError(502, "UPSTREAM_ERROR_INVALID");
    }
    throw new HubSpotCompanyServiceError(response.status, code);
  }
  const page = parseHubSpotCompanySearchPage(payload);
  if (!page || page.nextCursor !== null) {
    throw new HubSpotCompanyServiceError(502, "UPSTREAM_RESPONSE_INVALID");
  }
  return page.results.map(toLegacyHubSpotCompany);
}
const COMPANY_LIFECYCLE_STAGE_CUSTOMER_ID = "customer";
const COMPANY_LIFECYCLE_STAGE_OPPORTUNITY_ID = "opportunity";
const COMPANY_CUSTOMER_OR_OPPORTUNITY_STAGES = new Set([
  COMPANY_LIFECYCLE_STAGE_CUSTOMER_ID,
  COMPANY_LIFECYCLE_STAGE_OPPORTUNITY_ID,
]);

const mapOptions = (options: HubSpotRawOption[]): HubSpotPropertyOption[] =>
  options
    .map((option) => ({
      label: option.label,
      value: option.value,
      displayOrder: option.displayOrder,
      hidden: option.hidden,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

export interface HubSpotCompanyDuplicateMatch {
  id: string;
  name: string;
  razonSocial: string;
  domain: string;
  similarity: number;
}

export interface HubSpotCompanyDuplicateCheckResult {
  exists: boolean;
  matches: HubSpotCompanyDuplicateMatch[];
  similarMatches: HubSpotCompanyDuplicateMatch[];
}

export interface HubSpotCompanyCifDuplicateMatch {
  id: string;
  name: string;
  razonSocial: string;
  cif: string;
  domain: string;
}

export interface HubSpotCompanyCifDuplicateCheckResult {
  exists: boolean;
  matches: HubSpotCompanyCifDuplicateMatch[];
}

export interface HubSpotCompanyDomainDuplicateMatch {
  id: string;
  name: string;
  razonSocial: string;
  domain: string;
}

export interface HubSpotCompanyDomainDuplicateCheckResult {
  exists: boolean;
  matches: HubSpotCompanyDomainDuplicateMatch[];
}

export async function searchCompaniesByName(
  name: string,
  ownerId?: string
): Promise<HubSpotCompany[]> {
  void ownerId;
  const trimmedName = name?.trim();
  if (trimmedName.length < 2) return [];

  const page = await searchCompaniesFromService(trimmedName, 100);
  return page.results.map(toLegacyHubSpotCompany);
}

export async function searchCompaniesWithDeals(
  name: string,
  ownerId?: string
): Promise<HubSpotCompany[]> {
  void ownerId;
  const companies = await searchCompaniesByName(name);
  if (companies.length === 0) return [];

  const companyIds = companies.map(company => company.id);
  const companyToDeals = await batchReadAssociations("companies", "deals", companyIds);

  return companies.filter((company) => {
    const deals = companyToDeals.get(company.id) || [];
    return deals.length > 0;
  });
}

export async function searchOurCustomerCompaniesByName(
  name: string,
  ownerId?: string
): Promise<HubSpotCompany[]> {
  void ownerId;
  const companies = await searchCompaniesByName(name);
  if (companies.length === 0) return [];

  const customerCompanies = companies.filter(
    (company) => company.properties?.lifecyclestage === COMPANY_LIFECYCLE_STAGE_CUSTOMER_ID
  );
  return customerCompanies;
}

export async function searchCustomerOrOpportunityCompaniesByName(
  name: string,
  ownerId?: string
): Promise<HubSpotCompany[]> {
  void ownerId;
  const companies = await searchCompaniesByName(name);
  if (companies.length === 0) return [];

  const trimmedName = name?.trim();

  const isCustomerOrOpportunity = (company: HubSpotCompany) =>
    COMPANY_CUSTOMER_OR_OPPORTUNITY_STAGES.has(company.properties?.lifecyclestage || "");

  if (!trimmedName) {
    return companies.filter(isCustomerOrOpportunity);
  }

  return companies;
}

export async function getCompanyById(id: string): Promise<HubSpotCompany> {
  return toLegacyHubSpotCompany(await getCompanyFromServiceV2(id.trim()));
}

export async function getIndustryOptions(): Promise<HubSpotPropertyOption[]> {
  try {
    const options = await getHubSpotPropertyOptions("companies", "industria");
    return mapOptions(options);
  } catch (error) {
    console.error("Error fetching industry options:", error);
    throw new Error("Failed to fetch industry options");
  }
}

export async function getCompanyProvinciaOptions(): Promise<HubSpotPropertyOption[]> {
  try {
    const options = await getHubSpotPropertyOptions("companies", "provincia");
    return mapOptions(options);
  } catch (error) {
    console.error("Error fetching provincia options:", error);
    throw new Error("Failed to fetch provincia options");
  }
}

export async function getCompanyTypeOptions(): Promise<HubSpotPropertyOption[]> {
  try {
    const options = await getHubSpotPropertyOptions("companies", "type");
    return mapOptions(options);
  } catch (error) {
    console.error("Error fetching company type options:", error);
    throw new Error("Failed to fetch company type options");
  }
}
